import type { McpChatMessage } from "../shared/mcp-chat-types";
import { formatAgentDisplayName } from "./mcp-chat-specialist-report-parser";
import { getToolGroupLabel } from "./mcp-tool-group-labels";

export interface ToolTranscriptActivity {
  requestId: string;
  toolCallId?: string;
  toolName: string;
  status: "running" | "completed" | "error" | "blocked";
  isMutating: boolean;
  description: string;
  input?: Record<string, any>;
  result?: any;
  error?: string;
  startTime?: number;
  duration?: number;
  /** Set when this activity is a specialist-agent run (e.g. "wirer", "binder"). Carries the raw agent id. */
  specialistAgent?: string;
  /** Final accumulated specialist text (parseable JSON) once status is completed/error. */
  specialistAccumulated?: string;
}

export const SPECIALIST_TOOL_PREFIX = "specialist:";

function isSpecialistActivity(activity: Pick<ToolTranscriptActivity, "toolName" | "specialistAgent">): boolean {
  return !!activity.specialistAgent || activity.toolName.startsWith(SPECIALIST_TOOL_PREFIX);
}

function extractSpecialistAgent(activity: Pick<ToolTranscriptActivity, "toolName" | "specialistAgent">): string {
  if (activity.specialistAgent) return activity.specialistAgent;
  return activity.toolName.slice(SPECIALIST_TOOL_PREFIX.length);
}

export interface ToolTranscriptGroup {
  key: string;
  label: string;
  toolName: string;
  operation: string | null;
  activities: ToolTranscriptActivity[];
  completedCount: number;
  runningCount: number;
  errorCount: number;
}

export function getToolActivityStatusText(activity: Pick<ToolTranscriptActivity, "status">): string {
  if (activity.status === "running") {
    return "Running";
  }
  if (activity.status === "error") {
    return "Failed";
  }
  return "Completed";
}

export function getToolTranscriptGroupKey(
  activity: Pick<ToolTranscriptActivity, "toolName" | "input" | "specialistAgent">
): string {
  // Specialist activities collapse to one group per agent across turns so
  // three Binder runs render as a single "Binder · 3 runs" entry instead
  // of three stacked bubbles.
  if (isSpecialistActivity(activity)) {
    return `${SPECIALIST_TOOL_PREFIX}${extractSpecialistAgent(activity)}`;
  }
  const operation = typeof activity.input?.operation === "string" ? activity.input.operation : "";
  return operation ? `${activity.toolName}:${operation}` : activity.toolName;
}

export function getToolTranscriptGroupOperation(activity: Pick<ToolTranscriptActivity, "input">): string | null {
  return typeof activity.input?.operation === "string" ? activity.input.operation : null;
}

export function getToolTranscriptGroupLabel(
  activity: Pick<ToolTranscriptActivity, "toolName" | "input" | "description" | "specialistAgent">
): string {
  if (isSpecialistActivity(activity)) {
    return formatAgentDisplayName(extractSpecialistAgent(activity));
  }
  return getToolGroupLabel(activity.toolName, activity.input) || activity.description || activity.toolName;
}

export function isSpecialistTranscriptActivity(
  activity: Pick<ToolTranscriptActivity, "toolName" | "specialistAgent">
): boolean {
  return isSpecialistActivity(activity);
}

export function getSpecialistAgentFromActivity(
  activity: Pick<ToolTranscriptActivity, "toolName" | "specialistAgent">
): string {
  return extractSpecialistAgent(activity);
}

export function groupToolActivitiesForTranscript(activities: ToolTranscriptActivity[]): ToolTranscriptGroup[] {
  const groups = new Map<string, ToolTranscriptGroup>();

  for (const activity of activities) {
    const groupKey = getToolTranscriptGroupKey(activity);
    const existing = groups.get(groupKey);
    if (existing) {
      existing.activities.push(activity);
      if (activity.status === "error") existing.errorCount++;
      else if (activity.status === "running") existing.runningCount++;
      else existing.completedCount++;
      continue;
    }

    groups.set(groupKey, {
      key: `${groupKey}:${activity.toolCallId || activity.requestId}`,
      label: getToolTranscriptGroupLabel(activity),
      toolName: activity.toolName,
      operation: getToolTranscriptGroupOperation(activity),
      activities: [activity],
      completedCount: activity.status === "completed" ? 1 : 0,
      runningCount: activity.status === "running" ? 1 : 0,
      errorCount: activity.status === "error" ? 1 : 0
    });
  }

  return Array.from(groups.values());
}

export function sortGroupsRunningLast(groups: ToolTranscriptGroup[]): ToolTranscriptGroup[] {
  const done: ToolTranscriptGroup[] = [];
  const running: ToolTranscriptGroup[] = [];
  for (const group of groups) {
    if (group.runningCount > 0) {
      running.push(group);
    } else {
      done.push(group);
    }
  }
  if (running.length === 0 || done.length === 0) return groups;
  return done.concat(running);
}

export function buildToolTranscriptSearchText(activities?: ToolTranscriptActivity[]): string {
  if (!activities || activities.length === 0) return "";

  return groupToolActivitiesForTranscript(activities)
    .flatMap((group) => [
      group.label,
      ...group.activities.flatMap((activity) => [activity.description, getToolActivityStatusText(activity)])
    ])
    .filter((value): value is string => !!value && value.trim().length > 0)
    .join("\n");
}

export function buildMessageSearchText(message: Pick<McpChatMessage, "content" | "completedToolActivities">): string {
  const transcriptText = buildToolTranscriptSearchText(message.completedToolActivities);
  if (!transcriptText) return message.content;
  if (!message.content) return transcriptText;
  return `${message.content}\n${transcriptText}`;
}
